import React, { useEffect, useRef, useState } from 'react';
import { Alert, AppState, Linking, Platform, TouchableOpacity, Text, View, StyleSheet, ActivityIndicator, StyleProp, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Colors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { errorReporter } from '../services/errorReporter';

/**
 * Mapea el idioma del store al código `hl` que aceptan Google Maps y Waze.
 * El usuario reportó que Google Maps le hablaba en inglés aunque su idioma
 * en el app fuera español — sin `hl` en el URL, Google Maps usa el locale
 * del sistema Android (que puede ser 'en-US' si el ROM viene así de fábrica).
 * Con `hl=es` forzamos español independientemente del ROM.
 */
function langCode(lang: 'es' | 'en' | 'ht' | undefined): string {
  if (lang === 'ht') return 'fr'; // Waze/Maps no tienen kreyòl; francés es el próximo mejor
  if (lang === 'en') return 'en';
  return 'es';
}

/**
 * NAVIGATION LAUNCHER
 * ----------------------------------------------------------------------------
 * Botón único que abre la app de navegación preferida del chofer hacia un
 * destino real. Maneja 4 apps:
 *   - Google Maps      (iOS / Android)
 *   - Waze             (iOS / Android)
 *   - Apple Maps       (iOS solo)
 *   - Sistema default  (fallback)
 *
 * REGLAS:
 *   - Si el chofer ya eligió antes una app preferida, abre directo sin
 *     preguntar (preferencia persistida en AsyncStorage).
 *   - Si toca el botón con un long-press, le re-aparece el menú de elección
 *     para cambiar de app.
 *   - Si la app preferida no está instalada, fallback automático a Google
 *     Maps Web (siempre funciona porque solo requiere navegador).
 *   - Detecta dinámicamente qué apps están instaladas (canOpenURL).
 *
 * USO:
 *   <NavigationLauncher
 *     destinationLat={18.4731}
 *     destinationLng={-69.9329}
 *     destinationLabel="Plaza Naco"
 *   />
 */

const PREF_KEY = 'gs_nav_app_preference';

// La burbuja flotante y el botón de la hoja del viaje son DOS instancias de este
// componente en la misma pantalla. Cada una leía la preferencia al montarse: al
// cambiar la app en una, la otra seguía abriendo la anterior. Con este aviso a
// nivel de módulo, elegir la app en cualquiera cambia las dos en el acto.
const suscriptoresPreferencia = new Set<(app: NavApp) => void>();

type NavApp = 'google_maps' | 'waze' | 'apple_maps' | 'system';

interface Props {
  /**
   * Coordenadas del destino. Pueden faltar: un destino escrito a mano llega sin
   * punto (ver functions/src/destino.ts). Sin coordenadas válidas se navega por
   * `destinoTexto`; sin ninguna de las dos cosas el componente no se pinta.
   */
  destinationLat?: number | null;
  destinationLng?: number | null;
  destinationLabel?: string;
  /**
   * La dirección escrita (con su sector) para navegar cuando NO hay punto en el
   * mapa. Google Maps la busca y arranca la ruta; Waze solo sabe BUSCARLA, así
   * que antes de abrirlo se le dice al chofer que toque el resultado correcto.
   */
  destinoTexto?: string;
  /**
   * El viaje al que pertenece este destino. Solo se usa para RESCATAR el punto
   * cuando el viaje llegó sin coordenadas: ver `conseguirPunto`. Sin él, la
   * navegación por texto sigue funcionando igual que antes.
   */
  rideId?: string | null;
  /** Si pasas el origen, lo añade a Google Maps (Waze no lo soporta) */
  originLat?: number;
  originLng?: number;
  /** Personalizar look del botón */
  compact?: boolean;
  /**
   * 'burbuja': botón redondo FLOTANTE sobre el mapa, con el rótulo de a dónde
   * lleva y con qué app, y un toque aparte para cambiar de app. Lo pidió el
   * fundador: el botón de siempre queda al final de la hoja del viaje y el
   * chofer no lo encuentra, justo cuando tiene que salir a recoger o arrancar
   * hacia el destino. La lógica de apertura es la MISMA en las dos variantes.
   */
  variante?: 'boton' | 'burbuja';
  /** Rótulo de la burbuja: "Ir a recoger", "Ir al destino". */
  etiqueta?: string;
  /** Posición de la burbuja sobre el mapa (la decide la pantalla). */
  estiloContenedor?: StyleProp<ViewStyle>;
}

export default function NavigationLauncher({
  destinationLat,
  destinationLng,
  destinationLabel,
  destinoTexto,
  rideId,
  originLat,
  originLng,
  compact = false,
  variante = 'boton',
  etiqueta,
  estiloContenedor,
}: Props) {
  const [preference, setPreference] = useState<NavApp | null>(null);
  const [loading, setLoading] = useState(false);
  // Idioma que el usuario eligió en el app — para forzar la voz correcta en
  // Google Maps / Waze. Sin esto, Google Maps hereda el locale del sistema
  // (que puede ser 'en-US' si el ROM viene así de fábrica) y le habla al
  // chofer en inglés aunque el app esté en español.
  const uiLang = useAppStore((s) => s.language) as 'es' | 'en' | 'ht' | undefined;

  // ── ¿HAY PUNTO, O SOLO UNA DIRECCIÓN ESCRITA? ──────────────────────────────
  // Antes las coordenadas se pegaban tal cual en el enlace: la burbuja filtraba
  // null y (0,0), pero el botón de la hoja solo miraba `!= null`, así que un 0 o
  // un NaN habría abierto Waze en `ll=0,0` (el Golfo de Guinea) o `ll=NaN,NaN`.
  // Ahora lo decide el propio componente, igual para las dos variantes.
  const latNum = Number(destinationLat);
  const lngNum = Number(destinationLng);
  const hayPunto =
    destinationLat != null && destinationLng != null &&
    Number.isFinite(latNum) && Number.isFinite(lngNum) &&
    Math.abs(latNum) <= 90 && Math.abs(lngNum) <= 180 &&
    !(latNum === 0 && lngNum === 0);
  const textoDestino = String(destinoTexto ?? '').trim() || String(destinationLabel ?? '').trim();
  const porTexto = !hayPunto && textoDestino.length > 0;
  const latTxt = hayPunto ? latNum.toFixed(6) : '';
  const lngTxt = hayPunto ? lngNum.toFixed(6) : '';
  const textoCodificado = encodeURIComponent(textoDestino);

  // ── EL AVISO QUE PIDIÓ EL FUNDADOR: «SI WAZE NO TE FUNCIONA, USA GOOGLE MAPS» ─
  // No se puede disparar al DETECTAR el fallo: Android no avisa si Waze trazó
  // la ruta o se quedó en la vista previa (`openURL` resuelve en cuanto se lanza
  // la app). Así que sale en los dos momentos en que el chofer lo puede leer y
  // actuar: justo al lanzar Waze (si Waze no llega a abrir, lo ve en el acto) y
  // cuando vuelve a Go Safely poco después (lo normal si Waze no le sirvió). El
  // botón abre Google Maps SOLO esta vez, sin cambiar su app preferida.
  const [avisoWaze, setAvisoWaze] = useState(false);
  const wazeLanzadoEnRef = useRef(0);
  const temporizadorAvisoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mostrarAvisoWaze = () => {
    setAvisoWaze(true);
    if (temporizadorAvisoRef.current) clearTimeout(temporizadorAvisoRef.current);
    temporizadorAvisoRef.current = setTimeout(() => setAvisoWaze(false), 25_000);
  };
  useEffect(() => {
    const sub = AppState.addEventListener('change', (estado) => {
      if (estado !== 'active') return;
      if (wazeLanzadoEnRef.current && Date.now() - wazeLanzadoEnRef.current < 3 * 60_000) {
        // Se consume: sale una vez al lanzar y una en la primera vuelta a la
        // app, no en cada vuelta (una llamada, un chat…) durante 3 minutos.
        wazeLanzadoEnRef.current = 0;
        mostrarAvisoWaze();
      }
    });
    return () => {
      sub.remove();
      if (temporizadorAvisoRef.current) clearTimeout(temporizadorAvisoRef.current);
    };
  }, []);

  useEffect(() => {
    let vivo = true;
    // Si otra instancia avisó un cambio mientras se leía el disco, gana el aviso:
    // la lectura puede traer el valor de antes.
    let avisada = false;
    AsyncStorage.getItem(PREF_KEY)
      .then((v) => { if (vivo && !avisada) setPreference((v as NavApp) ?? null); })
      .catch(() => {});
    const alCambiar = (app: NavApp) => { avisada = true; setPreference(app); };
    suscriptoresPreferencia.add(alCambiar);
    return () => {
      vivo = false;
      suscriptoresPreferencia.delete(alCambiar);
    };
  }, []);

  const buildUrl = (app: NavApp): string => {
    // Con punto se navega al punto; sin él, a la dirección escrita (el destino
    // ya codificado para ir dentro de un enlace).
    const destino = hayPunto ? `${latTxt},${lngTxt}` : textoCodificado;
    const hl = langCode(uiLang);
    switch (app) {
      case 'google_maps':
        if (Platform.OS === 'ios') {
          // iOS: comgooglemaps:// no acepta hl. La voz sigue la config de la
          // app Google Maps. Al menos aseguramos directionsmode=driving.
          return `comgooglemaps://?daddr=${destino}&directionsmode=driving${originLat ? `&saddr=${originLat},${originLng}` : ''}`;
        }
        // Android: google.navigation acepta hl para voz + labels de calles, y
        // con texto BUSCA la dirección y arranca la ruta sola.
        return `google.navigation:q=${destino}&mode=d&hl=${hl}`;
      case 'waze':
        // Solo de referencia: Waze NO pasa por aquí. `openApp` lo desvía a
        // `abrirWaze`, que prueba primero el esquema y después el enlace
        // universal (ver allí por qué ese orden).
        return urlWazeApp();
      case 'apple_maps':
        return `maps://?daddr=${destino}&dirflg=d`;
      case 'system':
      default:
        if (Platform.OS === 'ios') {
          return `maps://?daddr=${destino}`;
        }
        return hayPunto
          ? `geo:${latTxt},${lngTxt}?q=${latTxt},${lngTxt}(${encodeURIComponent(destinationLabel ?? 'Destino')})`
          : `geo:0,0?q=${textoCodificado}`;
    }
  };

  // ══ WAZE: LAS DOS FORMAS, Y PARA QUE SIRVE CADA UNA ════════════════
  //
  // `utm_source` lo pide la propia documentación de Waze: es lo que permite que
  // su soporte ayude si un enlace no funciona. No cuesta nada llevarlo.
  // Sin punto, Waze solo puede BUSCAR la dirección (`q=`): su documentación no
  // garantiza que `navigate=yes` arranque sin `ll`, y en la práctica abre la
  // lista de resultados. Por eso antes se le avisa al chofer (avisarWazePorTexto).
  const urlWazeUniversal = (): string =>
    hayPunto
      ? `https://waze.com/ul?ll=${latTxt},${lngTxt}&navigate=yes&utm_source=gosafely`
      : `https://waze.com/ul?q=${textoCodificado}&navigate=yes&utm_source=gosafely`;

  const urlWazeApp = (): string =>
    hayPunto
      ? `waze://?ll=${latTxt},${lngTxt}&navigate=yes&utm_source=gosafely`
      : `waze://?q=${textoCodificado}&navigate=yes&utm_source=gosafely`;

  /**
   * ABRIR WAZE — y si no se puede, DECIRLO en vez de mentir.
   *
   * ═══ POR QUE EXISTE ESTA FUNCION ═══
   *
   * El fundador lo reporta desde hace rondas: "la navegación con Waze sigue con
   * fallo, Google Maps funciona excelente". Ya se intentó arreglar una vez
   * poniendo el esquema `waze://` PRIMERO, razonando que `canOpenURL(https)`
   * siempre da true y por eso se declaraba éxito en falso. Esa mitad del
   * razonamiento era correcta; la conclusión no.
   *
   * La documentación de Waze (developers.google.com/waze/deeplinks) dice lo
   * contrario de lo que hacíamos, y lo dice con su propio código de ejemplo:
   *
   *   · `waze://` se usa para COMPROBAR si la app está instalada.
   *   · `https://waze.com/ul?...` es la forma con la que se LANZA.
   *
   * La razón técnica: `https://waze.com/ul` es un App Link verificado de Waze.
   * Con Waze instalado, Android NO lo manda al navegador — lo entrega a Waze con
   * los parámetros intactos. Nosotros lanzábamos `waze://?ll=...`, que no es la
   * forma documentada, y tiene una trampa peor: la propia documentación advierte
   * que si Waze NO está instalado, con `waze://` **no pasa nada** — sin error y
   * sin aviso. O sea que el arreglo anterior cambió un fallo silencioso por otro
   * fallo silencioso.
   *
   * ⚠️ LO QUE NO PUEDO SABER DESDE AQUI, Y POR ESO ESTO AVISA.
   *    No tengo su teléfono delante ni sé qué versión de Waze tiene instalada, y
   *    las dos formas se comportan distinto según la versión y según si el ROM
   *    verificó los App Links. Así que esto no es otra adivinanza: prueba las
   *    formas EN EL ORDEN DOCUMENTADO y manda a Crashlytics cuál funcionó y
   *    cuál no. Después del próximo viaje, `python scripts/ver-fallos.py` nos da
   *    la respuesta con datos de su teléfono, no con mi opinión.
   */
  /**
   * CONSIGUE UN PUNTO PARA WAZE, AUNQUE EL VIAJE NO LO TRAIGA.
   *
   * ═══ EL FALLO QUE ESTO CIERRA ═══
   *
   * Medido en producción el 27-sep-2026: de los dos viajes completados, el del
   * 18-sep («Megacentro», que la pasajera escribió a mano sin elegir sugerencia)
   * NO tiene `destinationLat`/`destinationLng`. Google Maps resuelve ese texto
   * y traza la ruta —por eso el fundador lo ve «excelente»—, pero Waze con un
   * texto de búsqueda abre el mapa SIN RUTA. Eso es exactamente lo que él
   * reporta desde hace rondas: «sale en blanco».
   *
   * El servidor ya sabe resolverlo (`resolverDestinoDelViaje`), así que se le
   * pide AQUÍ, con el chofer esperando, y de paso queda guardado en el viaje
   * para la línea de la ruta y para la próxima vez.
   *
   * Devuelve `null` si no hay forma: entonces se le dice al chofer, que es
   * mejor que abrirle Waze vacío y dejarlo pensando que la app está rota.
   */
  const conseguirPunto = async (): Promise<{ lat: string; lng: string } | null> => {
    if (hayPunto) return { lat: latTxt, lng: lngTxt };
    if (!rideId) return null;
    try {
      const { callResolverDestinoDelViaje } = await import('../services/cloudFunctions');
      const r: any = await callResolverDestinoDelViaje({ rideId: String(rideId) });
      const lat = Number(r?.data?.lat);
      const lng = Number(r?.data?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat: lat.toFixed(6), lng: lng.toFixed(6) };
    } catch (e: any) {
      console.warn('[navegar] no se pudo resolver el destino:', e?.message);
      return null;
    }
  };

  const abrirWaze = async (): Promise<boolean> => {
    // ══ 16-SEP-2026: POR QUÉ SEGUÍA FALLANDO, Y EL ORDEN NUEVO ══════════════
    //
    // El fundador volvió a reportar que con Waze no se puede navegar. La causa
    // está en la línea de abajo que hacía `openURL('https://waze.com/ul…')`
    // PRIMERO: ese `openURL` NUNCA lanza. Si en el teléfono Android no tiene
    // verificado el enlace de waze.com para la app Waze (pasa: depende de la
    // versión de Waze y del ROM), el enlace se entrega al NAVEGADOR, que abre
    // una página de Waze sin ruta — y aquí se daba por "abierto" y se cortaba.
    // Nunca se llegaba a probar el esquema, y a Crashlytics no llegaba nada
    // porque para el código no había fallado.
    //
    // El orden correcto cuando Waze SÍ está instalado es el esquema `waze://`:
    // es una dirección que solo la app Waze sabe abrir, así que Android no
    // puede mandarlo al navegador. La trampa que documenta Waze ("con `waze://`
    // no pasa nada si no está instalado") se evita PREGUNTANDO primero con
    // `canOpenURL('waze://')`, que dice la verdad porque el manifiesto declara
    // `<queries>` con el esquema waze y el paquete com.waze.
    let instalado = false;
    try {
      instalado = await Linking.canOpenURL('waze://');
    } catch {
      instalado = false;
    }
    if (!instalado) {
      // Sin Waze no hay enlace que valga: el llamador ofrece instalarlo o abrir
      // la ruta en el navegador. Se anota para saber si algún teléfono dice
      // "no instalado" teniéndolo.
      errorReporter.captureException(new Error('Waze no aparece instalado al intentar navegar'), {
        tag: 'navegacion-waze',
        severity: 'warning',
        extra: { plataforma: Platform.OS },
      });
      return false;
    }

    // ── WAZE SIN COORDENADAS ABRE EN BLANCO ─────────────────────────────
    // Antes, si el viaje venía sin punto se le mandaba el texto como búsqueda
    // (`q=`) y Waze abría el mapa sin trazar nada — el fallo que el fundador
    // reporta desde hace rondas, y que con Google Maps no se ve porque ese sí
    // sabe resolver una dirección escrita. Aquí se consigue el punto ANTES.
    const punto = await conseguirPunto();
    if (!punto) {
      Alert.alert(
        'No podemos trazar la ruta en Waze',
        'Este viaje llegó sin un punto exacto en el mapa y Waze necesita uno. Navega con Google Maps, que sí sabe buscar la dirección escrita, o pídele a tu pasajero la dirección exacta.',
        [{ text: 'Entendido' }]
      );
      errorReporter.captureMessage('waze sin punto: no se pudo resolver el destino', {
        tag: 'navegacion-waze',
        extra: { texto: textoDestino.slice(0, 120), rideId: String(rideId ?? '') },
      });
      return false;
    }
    const conPunto = (base: string) =>
      `${base}?ll=${punto.lat},${punto.lng}&navigate=yes&utm_source=gosafely`;

    const intentos: Array<{ como: string; url: string }> = [
      // 1o el esquema de la app: solo Waze lo abre, nunca el navegador.
      { como: 'esquema-app', url: conPunto('waze://') },
      // 2o la forma universal, por si esta versión de Waze no atiende el esquema.
      { como: 'universal-https', url: conPunto('https://waze.com/ul') },
    ];

    const falladas: string[] = [];
    for (const intento of intentos) {
      try {
        await Linking.openURL(intento.url);
        // Miga de pan, no incidencia: no se ensucia Crashlytics cuando todo va
        // bien, pero si algo falla después sabremos por cuál forma entró.
        errorReporter.captureMessage(`waze abierto por ${intento.como}`, {
          tag: 'navegacion-waze',
          extra: {
            como: intento.como,
            fallaronAntes: falladas.join(',') || 'ninguna',
            porTexto: porTexto ? 'si' : 'no',
          },
        });
        wazeLanzadoEnRef.current = Date.now();
        mostrarAvisoWaze();
        return true;
      } catch (e: any) {
        // El error de `openURL` repite la URL y, en Android 12 o anterior, el
        // Intent con el destino DECODIFICADO (con espacios): ninguna limpieza
        // por expresión regular lo corta bien. A Crashlytics va solo QUÉ tipo de
        // fallo fue, nunca el texto: la política de privacidad dice que esos
        // informes son técnicos.
        const crudo = String(e?.message ?? '');
        const tipo = /No Activity found/i.test(crudo)
          ? 'sin-actividad'
          : /Permission Denial|SecurityException/i.test(crudo)
            ? 'permiso'
            : crudo ? 'otro' : 'sin-mensaje';
        falladas.push(`${intento.como}: ${tipo}`);
      }
    }

    // Las DOS formas fallaron. Esto sí es una incidencia: sale en Crashlytics y
    // la vemos con scripts/ver-fallos.py sin preguntarle nada a nadie.
    errorReporter.captureException(
      new Error('No se pudo abrir Waze por ninguna de las dos formas'),
      {
        tag: 'navegacion-waze',
        severity: 'error',
        extra: { intentos: falladas.join(' | '), plataforma: Platform.OS },
      }
    );
    return false;
  };

  const fallbackWebUrl = (): string => {
    const hl = langCode(uiLang);
    // hl fuerza el idioma de la interfaz web. dir_action=navigate arranca
    // la navegación desde la ubicación actual en vez de solo mostrar la
    // ruta como preview.
    const destino = hayPunto ? `${latTxt},${lngTxt}` : textoCodificado;
    return `https://www.google.com/maps/dir/?api=1&destination=${destino}&travelmode=driving&dir_action=navigate&hl=${hl}`;
  };

  /**
   * Google Maps SOLO ESTA VEZ, sin tocar la app preferida: es la salida del
   * aviso de Waze. Si ni la app abre, va al navegador, que siempre funciona.
   */
  const abrirGoogleMapsEstaVez = async (): Promise<void> => {
    setAvisoWaze(false);
    wazeLanzadoEnRef.current = 0;
    const ok = await openApp('google_maps');
    if (!ok) Linking.openURL(fallbackWebUrl()).catch(() => {});
  };

  /**
   * Waze sin punto en el mapa: Waze va a BUSCAR la dirección, no a arrancar la
   * ruta. Se le dice al chofer ANTES de mandarlo, con la salida a Google Maps
   * (que sí arranca sola con texto) en el mismo diálogo. Tres botones como
   * máximo: Android descarta en silencio a partir del cuarto.
   */
  const avisarWazePorTexto = (): Promise<boolean> =>
    new Promise((resolver) => {
      Alert.alert(
        'Waze va a buscar la dirección',
        `Este destino no tiene punto exacto en el mapa. Waze buscará «${textoDestino}»: toca el resultado correcto para arrancar. Si Waze no lo encuentra, usa Google Maps.`,
        [
          { text: 'Usar Google Maps', onPress: () => { abrirGoogleMapsEstaVez().finally(() => resolver(true)); } },
          { text: 'Abrir Waze', onPress: () => { abrirWaze().then(resolver).catch(() => resolver(false)); } },
        ],
        { cancelable: true, onDismiss: () => resolver(true) }
      );
    });

  const openApp = async (app: NavApp): Promise<boolean> => {
    setLoading(true);
    try {
      // Waze tiene su propia cascada: dos formas de enlace, en el orden que
      // manda su documentación, y avisando si las dos fallan. Ver `abrirWaze`.
      // Sin punto, primero se avisa de que Waze solo va a buscar.
      if (app === 'waze') return porTexto ? await avisarWazePorTexto() : await abrirWaze();
      // Se va a usar otra app: el aviso de Waze ya no tiene sentido.
      wazeLanzadoEnRef.current = 0;
      setAvisoWaze(false);

      const url = buildUrl(app);
      // canOpenURL puede dar FALSOS NEGATIVOS aún con los schemes declarados
      // en el manifest (algunos ROMs). Estrategia: si canOpenURL dice sí,
      // abrimos. Si dice no, IGUAL intentamos openURL dentro de try/catch —
      // si la app existe, abrirá; si no, openURL lanza y caemos al fallback.
      let canOpen = false;
      try { canOpen = await Linking.canOpenURL(url); } catch { canOpen = false; }
      if (canOpen) {
        await Linking.openURL(url);
        return true;
      }
      // Segundo intento a ciegas — cubre el falso negativo de canOpenURL.
      try {
        await Linking.openURL(url);
        return true;
      } catch {
        // Aquí vivía un "tercer intento, solo Waze" que probaba la otra forma
        // de enlace. Ya no puede llegar nada de Waze a este punto: `openApp`
        // desvía Waze a `abrirWaze()` en su primera línea, que es donde están
        // ahora las dos formas y el aviso a Crashlytics. TypeScript lo señaló
        // como código imposible (TS2367) y tenía razón: mantenerlo aquí haría
        // creer a quien lo lea que Waze pasa por este camino, y no pasa.
        return false;
      }
    } catch (e) {
      console.warn('[NavigationLauncher] error abriendo', app, e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const storeUrlFor = (app: NavApp): string | null => {
    if (app !== 'waze') return null;
    if (Platform.OS === 'ios') return 'https://apps.apple.com/us/app/waze-navigation-live-traffic/id323229106';
    return 'https://play.google.com/store/apps/details?id=com.waze';
  };

  const openWithFallback = async (app: NavApp): Promise<void> => {
    const ok = await openApp(app);
    if (!ok) ofrecerInstalar(app);
  };

  /** Lo que se le ofrece al chofer cuando la app elegida no se pudo abrir. */
  const ofrecerInstalar = (app: NavApp): void => {
    const storeUrl = storeUrlFor(app);
    if (storeUrl) {
      // TRES BOTONES COMO MÁXIMO. En Android, `Alert` descarta en silencio a
      // partir del cuarto: aquí había cuatro y «Abrir en navegador» no salía
      // nunca. Cancelar ya no ocupa un botón: el diálogo se cierra con Atrás o
      // tocando fuera (`cancelable`), que antes tampoco se podía.
      Alert.alert(
        `${labelOf(app)} no instalado`,
        app === 'waze'
          ? 'Puedes instalar Waze, probar a abrirlo igual (algunos teléfonos dicen que no está teniéndolo) o seguir con Google Maps.'
          : `¿Quieres instalar ${labelOf(app)} para una mejor experiencia de navegación?`,
        [
          ...(app === 'waze'
            ? [
                { text: 'Usar Google Maps', onPress: () => { abrirGoogleMapsEstaVez(); } },
                // Falso negativo de canOpenURL: se deja probar igual.
                { text: 'Ya lo tengo, abrir', onPress: () => { Linking.openURL(urlWazeApp()).catch(() => Linking.openURL(urlWazeUniversal()).catch(() => {})); } },
              ]
            : [{ text: 'Abrir en navegador', onPress: () => { Linking.openURL(fallbackWebUrl()).catch(() => {}); } }]),
          {
            text: `Instalar ${labelOf(app)}`,
            onPress: () => { Linking.openURL(storeUrl).catch(() => Linking.openURL(fallbackWebUrl())); },
          },
        ],
        { cancelable: true }
      );
    } else {
      Alert.alert(
        `${labelOf(app)} no disponible`,
        '¿Quieres abrir Google Maps en el navegador?',
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Abrir en navegador', onPress: () => { Linking.openURL(fallbackWebUrl()).catch(() => {}); } },
        ],
        { cancelable: true }
      );
    }
  };

  const showAppPicker = async (saveAsPreference: boolean): Promise<void> => {
    // Detectar apps instaladas
    const checks = await Promise.all([
      Linking.canOpenURL(buildUrl('google_maps')).catch(() => false),
      // ⚠️ CON EL ESQUEMA PELADO, NO CON EL ENLACE https.
      // `canOpenURL('https://...')` SIEMPRE da true —cualquier navegador lo
      // abre—, así que Waze saldría en la lista aunque no esté instalado y el
      // chofer lo elegiría para nada. `waze://` dice la verdad porque el
      // manifiesto declara `<queries>` con el esquema waze y el paquete
      // com.waze (comprobado también DENTRO del APK ya compilado).
      Linking.canOpenURL('waze://').catch(() => false),
      Platform.OS === 'ios' ? Linking.canOpenURL('maps://').catch(() => false) : Promise.resolve(false),
    ]);
    const [hasGoogle, hasWaze, hasApple] = checks;

    const options: Array<{ text: string; onPress?: () => void; style?: 'cancel' | 'destructive' }> = [];

    if (hasGoogle) {
      options.push({
        text: 'Google Maps',
        onPress: () => {
          if (saveAsPreference) savePreference('google_maps');
          openWithFallback('google_maps');
        },
      });
    }
    if (hasWaze) {
      options.push({
        text: 'Waze',
        onPress: () => {
          if (saveAsPreference) savePreference('waze');
          openWithFallback('waze');
        },
      });
    }
    if (hasApple) {
      options.push({
        text: 'Apple Maps',
        onPress: () => {
          if (saveAsPreference) savePreference('apple_maps');
          openWithFallback('apple_maps');
        },
      });
    }
    // En Android `Alert` muestra 3 botones como máximo y el diálogo no se podía
    // cerrar con Atrás: con Google Maps, Waze, Navegador y Cancelar, se perdía
    // «Cancelar» y el chofer quedaba obligado a elegir. Ahora se cierra con Atrás
    // (cancelable) y el navegador solo se ofrece si hace falta sitio para él.
    const caben = Platform.OS === 'android' ? 3 : 6;
    if (options.length < caben) {
      options.push({
        text: 'Navegador web',
        onPress: () => { Linking.openURL(fallbackWebUrl()).catch(() => {}); },
      });
    }
    if (options.length < caben) options.push({ text: 'Cancelar', style: 'cancel' });

    Alert.alert(
      '¿Qué app prefieres?',
      saveAsPreference
        ? variante === 'burbuja'
          ? 'Guardaremos tu elección para no preguntarte cada vez. Para cambiarla, toca «Cambiar app» debajo del botón.'
          : 'Guardaremos tu elección para no preguntarte cada vez. Mantén presionado el botón si quieres cambiarla después.'
        : 'Cambia tu app preferida de navegación.',
      options,
      { cancelable: true }
    );
  };

  const savePreference = async (app: NavApp): Promise<void> => {
    // Avisa a todas las instancias montadas, incluida esta.
    suscriptoresPreferencia.forEach((fn) => fn(app));
    try { await AsyncStorage.setItem(PREF_KEY, app); } catch {}
  };

  const handlePress = async () => {
    if (loading) return;
    if (preference) {
      // Abrir directo con la preferida
      const ok = await openApp(preference);
      if (!ok) {
        // Waze elegido pero no instalado: se dice por qué y se ofrece instalarlo
        // (o ir por el navegador) en vez de abrir el selector sin explicación.
        if (preference === 'waze') {
          ofrecerInstalar(preference);
          return;
        }
        // App preferida desinstalada → volver a mostrar picker
        showAppPicker(true);
      }
      return;
    }
    showAppPicker(true);
  };

  const handleLongPress = () => {
    showAppPicker(true);
  };

  const labelOf = (app: NavApp): string => ({
    google_maps: 'Google Maps',
    waze: 'Waze',
    apple_maps: 'Apple Maps',
    system: 'Mapas',
  }[app]);

  // El aviso de Waze, igual en las dos variantes (debajo del control).
  const avisoWazeVista = avisoWaze ? (
    <View style={styles.avisoWaze}>
      <Text style={styles.avisoWazeTexto} numberOfLines={2}>¿Waze no te marcó la ruta?</Text>
      <TouchableOpacity
        onPress={() => { abrirGoogleMapsEstaVez(); }}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel="Abrir la ruta en Google Maps esta vez"
      >
        <Text style={styles.avisoWazeAccion}>Abrir en Google Maps</Text>
      </TouchableOpacity>
    </View>
  ) : null;

  if (!hayPunto && !porTexto) return null;

  if (variante === 'burbuja') {
    return (
      <View style={[styles.burbujaContenedor, estiloContenedor]} pointerEvents="box-none">
        <TouchableOpacity
          style={styles.burbuja}
          onPress={handlePress}
          onLongPress={handleLongPress}
          delayLongPress={500}
          activeOpacity={0.85}
          disabled={loading}
          accessibilityRole="button"
          accessibilityLabel={`${etiqueta ?? 'Navegar'}${preference ? ` con ${labelOf(preference)}` : ''}`}
        >
          {loading ? (
            <ActivityIndicator color="#FFF" size="small" />
          ) : (
            <Ionicons name="navigate" size={28} color="#FFF" />
          )}
        </TouchableOpacity>
        <View style={styles.burbujaRotulo}>
          <Text style={styles.burbujaRotuloTexto} numberOfLines={1}>
            {etiqueta ?? 'Navegar'}
          </Text>
          <TouchableOpacity
            onPress={() => showAppPicker(true)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel="Cambiar la app de navegación"
          >
            <Text style={styles.burbujaCambiar} numberOfLines={1}>
              {preference ? `${labelOf(preference)} · Cambiar app` : 'Elegir app'}
            </Text>
          </TouchableOpacity>
        </View>
        {avisoWazeVista}
      </View>
    );
  }

  return (
    <View>
      <TouchableOpacity
        style={[styles.button, compact && styles.compact]}
        onPress={handlePress}
        onLongPress={handleLongPress}
        delayLongPress={500}
        activeOpacity={0.85}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#FFF" size="small" />
        ) : (
          <>
            <Ionicons name="navigate" size={compact ? 18 : 22} color="#FFF" />
            <Text style={[styles.text, compact && styles.textCompact]} numberOfLines={1}>
              {preference ? `Abrir ${labelOf(preference)}` : 'Navegar'}
            </Text>
          </>
        )}
      </TouchableOpacity>
      {avisoWazeVista}
    </View>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primary,
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 12,
    shadowColor: Colors.primary,
    shadowOpacity: 0.3,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 5,
  },
  compact: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
  },
  text: {
    color: '#FFF',
    fontWeight: '700',
    fontSize: 15,
    marginLeft: 8,
  },
  textCompact: {
    fontSize: 13,
  },
  // ── Burbuja flotante ─────────────────────────────────────────────────────
  // Mismo color y misma sombra que el botón de siempre: es el mismo control,
  // solo que a la vista. El rótulo va sobre fondo oscuro translúcido porque se
  // lee ENCIMA DEL MAPA, igual que el contador de carros del pasajero.
  burbujaContenedor: {
    alignItems: 'center',
  },
  burbuja: {
    width: 62,
    height: 62,
    borderRadius: 31,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primary,
    borderWidth: 3,
    borderColor: '#FFF',
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 8,
  },
  burbujaRotulo: {
    marginTop: 6,
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: 8,
    maxWidth: 150,
  },
  burbujaRotuloTexto: {
    color: '#FFF',
    fontWeight: '700',
    fontSize: 12,
  },
  burbujaCambiar: {
    color: '#FFD54F',
    fontWeight: '600',
    fontSize: 11,
    marginTop: 2,
  },
  // Aviso «¿Waze no te marcó la ruta?»: el mismo chip oscuro del rótulo, para
  // que se lea sobre el mapa y sobre la hoja; la acción en el amarillo de
  // «Cambiar app», que ya es el color de «esto se toca».
  avisoWaze: {
    marginTop: 6,
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
    maxWidth: 190,
  },
  avisoWazeTexto: {
    color: '#FFF',
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  avisoWazeAccion: {
    color: '#FFD54F',
    fontSize: 12,
    fontWeight: '800',
    marginTop: 3,
  },
});
